import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMessageReceipts } from '../api/chats';
import type { ReceiptDto } from '../api/types';
import Avatar from './Avatar';
import { backdropClose } from '../lib/modalBackdrop';
import { intlLocale } from '../lib/language';
import { formatReceiptTime } from '../lib/messageTime';

interface Props {
  chatId: string;
  messageId: number;
  onClose: () => void;
}

/** «Информация» о своём сообщении: по каждому получателю — когда доставлено и прочитано. */
export default function MessageInfoModal({ chatId, messageId, onClose }: Props) {
  const { t, i18n } = useTranslation('messageInfo');
  const locale = intlLocale(i18n.resolvedLanguage);
  const [rows, setRows] = useState<ReceiptDto[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getMessageReceipts(chatId, messageId)
      .then(setRows)
      .catch(() => setError(t('loadFailed')));
  }, [chatId, messageId, t]);

  return (
    <div className="modal-backdrop" {...backdropClose(onClose)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
        <h2>{t('title')}</h2>
        <button type="button" className="modal-close" title={t('close')} onClick={onClose}>✕</button>
        {error && <div className="form-error">{error}</div>}
        {!rows && !error && <div className="muted">{t('loading')}</div>}
        {rows && rows.length === 0 && <div className="muted">{t('empty')}</div>}
        {rows && rows.map((r) => (
          <div key={r.user.id} className="msg-info-row">
            <Avatar userId={r.user.id} name={r.user.displayName} size={32} version={r.user.avatarFileId} />
            <div className="msg-info-body">
              <div className="msg-info-name">{r.user.displayName}</div>
              <div className="msg-info-line">
                {t('delivered', { time: r.delivered ? formatReceiptTime(r.deliveredAt, locale) : t('not') })}
              </div>
              <div className="msg-info-line">
                {r.readHidden
                  ? t('readHidden')
                  : t('read', { time: r.read ? formatReceiptTime(r.readAt, locale) : t('not') })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
